import { useState, useEffect } from 'react';
import {
  CamundaAuthorizationService,
  CAMUNDA_PERMISSIONS,
  CAMUNDA_RESOURCE_TYPES,
} from '@/lib/camunda/authorization-service';
import { resolveProcessDefinitionResourceId } from '@/lib/camunda/process-definition-id';

export interface PermissionConfig {
  permissionName: string;
  resourceName: string;
  resourceType: number;
  resourceId?: string;
}

export interface PermissionResult {
  hasPermission: boolean;
  loading: boolean;
  error: string | null;
  isGlobalAdmin: boolean;
  source?: 'user' | 'group' | 'global';
}

const usePermissions = (config: PermissionConfig): PermissionResult => {
  const [result, setResult] = useState<PermissionResult>({
    hasPermission: false,
    loading: true,
    error: null,
    isGlobalAdmin: false,
  });

  const { permissionName, resourceName, resourceType, resourceId } = config;

  useEffect(() => {
    let cancelled = false;
    const checkPermissions = async () => {
      setResult((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const permissionResult =
          await CamundaAuthorizationService.checkUserPermission({
            permissionName,
            resourceName,
            resourceType,
            resourceId,
          });

        if (cancelled) {
          return;
        }

        setResult({
          hasPermission: permissionResult.hasAuthorization,
          loading: false,
          error: null,
          isGlobalAdmin: false,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setResult({
          hasPermission: false,
          loading: false,
          error: error instanceof Error ? error.message : 'Error desconocido',
          isGlobalAdmin: false,
        });
      }
    };

    void checkPermissions();

    return () => {
      cancelled = true;
    };
  }, [permissionName, resourceName, resourceType, resourceId]);

  return result;
};

export const useCanCreateProcess = (processKey?: string) => {
  return usePermissions({
    permissionName: CAMUNDA_PERMISSIONS.CREATE_INSTANCE,
    resourceName: 'PROCESS_DEFINITION',
    resourceType: CAMUNDA_RESOURCE_TYPES.PROCESS_DEFINITION,
    resourceId: processKey,
  });
};

export const useCanAssignTask = (
  processDefinitionId?: string,
  processDefinitionKey?: string
) => {
  const processKey = resolveProcessDefinitionResourceId(
    processDefinitionId,
    processDefinitionKey
  );
  return usePermissions({
    permissionName: CAMUNDA_PERMISSIONS.TASK_ASSIGN,
    resourceName: 'PROCESS_DEFINITION',
    resourceType: CAMUNDA_RESOURCE_TYPES.PROCESS_DEFINITION,
    resourceId: processKey,
  });
};

export const useCanCancelProcess = (
  processDefinitionId?: string,
  processDefinitionKey?: string
) => {
  const processKey = resolveProcessDefinitionResourceId(
    processDefinitionId,
    processDefinitionKey
  );
  return usePermissions({
    permissionName: CAMUNDA_PERMISSIONS.DELETE_INSTANCE,
    resourceName: 'PROCESS_DEFINITION',
    resourceType: CAMUNDA_RESOURCE_TYPES.PROCESS_DEFINITION,
    resourceId: processKey,
  });
};
